'use client';

import { Deployment } from '@/types/deployment';
import { isServer, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, ReactNode, Suspense, useEffect, useState } from 'react';
import ms from 'ms';

// Use production-safe DevTools that work even when NODE_ENV=production
const ReactQueryDevtoolsProduction = lazy(() =>
    import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({
        default: d.ReactQueryDevtools,
    }))
);

const THREE_HOURS = ms('3h');

const makeQueryClient = () => {
    return new QueryClient({
        defaultOptions: {
            queries: {
                initialDataUpdatedAt: Date.now() / 1000,
                refetchOnReconnect: false,
                refetchOnWindowFocus: false,
                retry: false,
                staleTime: THREE_HOURS,
            },
        },
    });
};

let browserQueryClient: QueryClient | undefined = undefined;

const getQueryClient = () => {
    if (isServer) {
        return makeQueryClient();
    }

    browserQueryClient ??= makeQueryClient();

    return browserQueryClient;
};

type Props = {
    children: ReactNode;
};

export const ReactQueryProvider = ({ children }: Props) => {
    const queryClient = getQueryClient();
    const [isShown, setIsShown] = useState(false);

    useEffect(() => {
        const deployment = process.env.NEXT_PUBLIC_DEPLOYMENT ?? Deployment.Dev;
        if (deployment !== Deployment.Prod) {
            // eslint-disable-next-line no-console
            console.warn('ReactQuery Devtools are available with the shortcut: Ctrl/Cmd + Shift + X');
        }

        const handleOpenReplayKeyPress = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'x') {
                setIsShown((prev) => !prev);
            }
        };
        document.addEventListener('keydown', handleOpenReplayKeyPress);
        return () => document.removeEventListener('keydown', handleOpenReplayKeyPress);
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            {isShown && (
                <Suspense fallback={null}>
                    <ReactQueryDevtoolsProduction initialIsOpen={false} />
                </Suspense>
            )}
            {children}
        </QueryClientProvider>
    );
};
